/**
 * カレンダー共通ツールバー
 *
 * 週/日どちらのビューでも同一デザインを保証する。
 * FullCalendar の headerToolbar を置き換える。
 */

import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'

export type CalendarViewMode = 'week' | 'day'

type Props = {
  title: string
  activeView: CalendarViewMode
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  onViewChange: (view: CalendarViewMode) => void
  onAutoSchedule?: () => void
  isScheduling?: boolean
}

const VIEW_BUTTONS: { key: CalendarViewMode; label: string }[] = [
  { key: 'week', label: '週' },
  { key: 'day', label: '日' },
]

export const CalendarToolbar = ({
  title,
  activeView,
  onPrev,
  onNext,
  onToday,
  onViewChange,
  onAutoSchedule,
  isScheduling,
}: Props) => {
  return (
    <div className="flex items-center justify-between p-2 bg-background flex-nowrap min-w-0">
      {/* 左: ナビゲーション */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          className="inline-flex items-center justify-center size-7 text-foreground hover:bg-muted transition-colors p-0!"
          onClick={onPrev}
        >
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          className="inline-flex items-center justify-center size-7 text-foreground hover:bg-muted transition-colors p-0!"
          onClick={onNext}
        >
          <ChevronRight className="size-4" />
        </button>
        <button type="button" className="text-sm! h-7 hover:bg-muted transition-colors ml-0.5" onClick={onToday}>
          今日
        </button>
        {onAutoSchedule && (
          <button
            type="button"
            disabled={isScheduling}
            className="inline-flex items-center gap-1 h-7 px-2! ml-1 rounded bg-zinc-800 text-zinc-100 text-xs hover:bg-zinc-700 transition-colors disabled:opacity-50 p-0! dark:bg-zinc-700 dark:hover:bg-zinc-600"
            onClick={onAutoSchedule}
          >
            <Sparkles className={`size-3.5 ${isScheduling ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">AI配置</span>
          </button>
        )}
      </div>

      {/* 中央: タイトル */}
      <span className="text-base font-semibold whitespace-nowrap">{title}</span>

      {/* 右: ビュー切替 */}
      <div className="inline-flex rounded border border-border overflow-hidden text-sm shrink-0">
        {VIEW_BUTTONS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`px-1! py-0.5! transition-colors first:border-l-0 ${
              activeView === key
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => onViewChange(key)}
            disabled={activeView === key}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}
